import { useCallback, useEffect, useRef } from 'react';

interface TimerOptions {
  interval?: number;      // 定时器间隔时间（毫秒）
  maxCount?: number;      // 最大执行次数，undefined 表示无限执行
  onComplete?: () => void; // 定时器完成时的回调
}

// 支持异步回调，并保证上一次执行完成后再开始下一轮
export const useTimer = (callback: () => void | Promise<void>, options: TimerOptions = {}) => {
  const {
    interval = 1000,
    maxCount,
    onComplete
  } = options;

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const countRef = useRef<number>(0);
  const callbackRef = useRef(callback);
  const runningRef = useRef<boolean>(false);
  const activeRef = useRef<boolean>(false);

  // 更新回调引用
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // 单步执行并在完成后调度下一次
  const tick = useCallback(async () => {
    // 避免重入
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      await callbackRef.current();
      countRef.current += 1;

      if (maxCount && countRef.current >= maxCount) {
        stop();
        onComplete?.();
        return;
      }
    } finally {
      runningRef.current = false;
    }
    // 在间隔后启动下一次执行
    if (activeRef.current) {
      timerRef.current = setTimeout(tick, interval);
    }
  }, [interval, maxCount, onComplete]);

  // 启动定时器（首次触发在 interval 后执行）
  const start = useCallback(() => {
    if (timerRef.current || runningRef.current || activeRef.current) return;
    activeRef.current = true;
    timerRef.current = setTimeout(tick, interval);
  }, [tick, interval]);

  // 停止定时器
  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    activeRef.current = false;
    runningRef.current = false;
  }, []);

  // 重置定时器
  const reset = useCallback(() => {
    stop();
    countRef.current = 0;
  }, [stop]);

  // 重启定时器
  const restart = useCallback(() => {
    reset();
    start();
  }, [reset, start]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    start,
    stop,
    reset,
    restart,
    isRunning: !!timerRef.current || runningRef.current,
    currentCount: countRef.current
  };
};